/**
 * MultifamilyQuoteForm — E10. Public Get-a-Quote form for multifamily
 * communities. Package data + ±range logic pulled live from the admin
 * Service Packages section (/api/service-packages/public).
 */
import { useEffect, useMemo, useState } from 'react';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { CheckCircle2 } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;
const GOLD = '#C9A962';

const TERM_LABELS = { '12': '12 months', '18': '18 months', '24': '24 months', '30': '30 months', '36': '36 months' };

const inputCls = 'w-full rounded-md px-3 py-2.5 text-sm text-white bg-[#1f1f1f] border-2 border-[#4a4a4a] focus:border-[#C9A962] focus:outline-none';
const labelCls = 'block text-xs font-bold uppercase tracking-widest mb-1.5';

export default function MultifamilyQuoteForm({ preselectPackage, prefill, onBack }) {
  const [catalog, setCatalog] = useState(null);
  const [form, setForm] = useState({
    full_name: (prefill && prefill.full_name) || '',
    property_name: (prefill && prefill.property_name) || '',
    property_address: (prefill && prefill.property_address) || '',
    unit_count: (prefill && (prefill.unit_count != null) ? String(prefill.unit_count) : ''),
    email: (prefill && prefill.email) || '',
    phone: (prefill && prefill.phone) || '',
    preferred_contact: 'email',
    preferred_starting_date: '',
    package_key: '', contract_term: '24',
  });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    fetch(`${API}/api/service-packages/public`)
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => {
        if (!d) return;
        setCatalog(d);
        setForm((f) => ({ ...f, package_key: f.package_key || d.packages?.[0]?.key || '' }));
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (!preselectPackage || !catalog) return;
    const match = catalog.packages.find((p) => p.name === preselectPackage);
    if (match) setForm((f) => ({ ...f, package_key: match.key }));
  }, [preselectPackage, catalog]);

  const selectedPkg = useMemo(
    () => catalog?.packages?.find((p) => p.key === form.package_key),
    [catalog, form.package_key]
  );
  const range = selectedPkg?.term_ranges?.[form.contract_term];
  const units = parseInt(form.unit_count, 10);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    setSubmitting(true);
    try {
      const res = await fetch(`${API}/api/service-packages/quote`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...form, unit_count: units || 0 }),
      });
      const body = await res.json();
      if (!res.ok) throw new Error(body?.detail?.[0]?.msg || body?.detail || 'Submission failed');
      setDone(body);
    } catch (err) {
      setError(typeof err.message === 'string' ? err.message : 'Please check your entries and try again.');
    }
    setSubmitting(false);
  };

  if (done) {
    const rng = done.estimate_range;
    return (
      <Card className="p-8 text-center" style={{ backgroundColor: '#2D2D2D', border: '2px solid #C9A962' }} data-testid="quote-form-success">
        <CheckCircle2 className="h-12 w-12 mx-auto mb-4" style={{ color: GOLD }} />
        <h3 className="text-2xl font-bold text-white mb-2" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>QUOTE REQUEST RECEIVED</h3>
        {rng && (
          <div className="my-5 rounded-lg p-5" style={{ backgroundColor: '#1f1f1f', border: '2px solid #C9A962' }} data-testid="quote-estimate-block">
            <p className="text-xs font-black uppercase tracking-[0.25em] mb-1" style={{ color: '#a0a0a0' }}>Your Preliminary Estimate</p>
            <p className="text-3xl font-black" style={{ color: GOLD, fontFamily: 'Barlow Condensed, sans-serif' }} data-testid="quote-estimate-range">
              {rng.low === rng.high ? `$${rng.low.toFixed(2)}` : `$${rng.low.toFixed(2)} – $${rng.high.toFixed(2)}`}
              <span className="text-base font-semibold ml-2" style={{ color: '#a0a0a0' }}>per unit / month</span>
            </p>
            {units > 0 && (
              <p className="text-sm mt-1 text-white" data-testid="quote-estimate-total">
                ≈ {rng.low === rng.high
                  ? `$${(rng.low * units).toLocaleString()}`
                  : `$${(rng.low * units).toLocaleString()} – $${(rng.high * units).toLocaleString()}`} / month for {units} units
              </p>
            )}
            <p className="text-xs mt-3 italic" style={{ color: '#a0a0a0' }} data-testid="quote-disclaimer">
              This is a preliminary estimate. Final pricing will be confirmed by our management team following a review of your submitted information.
            </p>
          </div>
        )}
        <p className="text-sm" style={{ color: '#a0a0a0' }}>
          Thank you — our management team will review your property details and follow up via your preferred contact method shortly.
        </p>
      </Card>
    );
  }

  return (
    <form onSubmit={submit} data-testid="multifamily-quote-form">
      {onBack && (
        <div className="mb-3">
          <button type="button" onClick={onBack}
                  className="text-[10px] uppercase font-bold tracking-widest px-3 py-1.5 rounded"
                  style={{ backgroundColor: '#27272a', color: '#fff' }}
                  data-testid="multifamily-quote-back">
            ← Back to Recommended Services
          </button>
        </div>
      )}
      <Card className="p-6 sm:p-8" style={{ backgroundColor: '#2D2D2D', border: '2px solid #4a4a4a' }}>
        <div className="grid sm:grid-cols-2 gap-5">
          <div>
            <label className={labelCls} style={{ color: GOLD }}>Full Name *</label>
            <input required className={inputCls} value={form.full_name} onChange={set('full_name')} data-testid="quote-full-name" />
          </div>
          <div>
            <label className={labelCls} style={{ color: GOLD }}>Property Name *</label>
            <input required className={inputCls} value={form.property_name} onChange={set('property_name')} data-testid="quote-property-name" />
          </div>
          <div className="sm:col-span-2">
            <label className={labelCls} style={{ color: GOLD }}>Property Address *</label>
            <input required className={inputCls} value={form.property_address} onChange={set('property_address')} data-testid="quote-property-address" />
          </div>
          <div>
            <label className={labelCls} style={{ color: GOLD }}>Number of Units *</label>
            <input required type="number" min="1" className={inputCls} value={form.unit_count} onChange={set('unit_count')} data-testid="quote-unit-count" />
          </div>
          <div>
            <label className={labelCls} style={{ color: GOLD }}>Email Address *</label>
            <input required type="email" className={inputCls} value={form.email} onChange={set('email')} data-testid="quote-email" />
          </div>
          <div>
            <label className={labelCls} style={{ color: GOLD }}>Phone Number *</label>
            <input required className={inputCls} value={form.phone} onChange={set('phone')} data-testid="quote-phone" />
          </div>
          <div>
            <label className={labelCls} style={{ color: GOLD }}>Preferred Contact Method *</label>
            <select className={inputCls} value={form.preferred_contact} onChange={set('preferred_contact')} data-testid="quote-preferred-contact">
              <option value="email">Email</option>
              <option value="phone">Phone Call</option>
              <option value="text">Text Message</option>
            </select>
          </div>
          <div>
            <label className={labelCls} style={{ color: GOLD }}>Service Package *</label>
            <select className={inputCls} value={form.package_key} onChange={set('package_key')} data-testid="quote-package-select">
              {(catalog?.packages || []).map((p) => (
                <option key={p.key} value={p.key}>{p.name}</option>
              ))}
            </select>
          </div>
          <div>
            <label className={labelCls} style={{ color: GOLD }}>Contract Term *</label>
            <select className={inputCls} value={form.contract_term} onChange={set('contract_term')} data-testid="quote-term-select">
              {(catalog?.contract_terms || ['12', '18', '24', '30', '36']).map((t) => (
                <option key={t} value={t}>{TERM_LABELS[t] || `${t} months`}</option>
              ))}
            </select>
          </div>
          <div className="md:col-span-2">
            <label className={labelCls} style={{ color: GOLD }}>Preferred Starting Date *</label>
            <input type="date" className={inputCls} value={form.preferred_starting_date}
                   onChange={set('preferred_starting_date')} required
                   data-testid="quote-preferred-start-date" />
          </div>
        </div>

        {(() => {
          const filled = form.full_name && form.property_name && form.property_address && units > 0 && form.email && form.phone;
          if (!filled) {
            return (
              <div className="mt-6 rounded-lg p-4 text-center" style={{ backgroundColor: '#1f1f1f', border: '1px solid #4a4a4a' }} data-testid="quote-pre-submit-note">
                <p className="text-xs italic" style={{ color: '#a0a0a0' }}>
                  Fill in your quote information above to see your preliminary estimate for the selected package and term.
                </p>
              </div>
            );
          }
          return range ? (
            <div className="mt-6 rounded-lg p-5 text-center" style={{ backgroundColor: '#1f1f1f', border: '2px solid #C9A962' }} data-testid="quote-estimate-block">
              <p className="text-xs font-black uppercase tracking-[0.25em] mb-1" style={{ color: '#a0a0a0' }}>Estimated Monthly Investment</p>
              <p className="text-3xl font-black" style={{ color: GOLD, fontFamily: 'Barlow Condensed, sans-serif' }} data-testid="quote-estimate-range">
                {range.low === range.high ? `$${range.low.toFixed(2)}` : `$${range.low.toFixed(2)} – $${range.high.toFixed(2)}`}
                <span className="text-base font-semibold ml-2" style={{ color: '#a0a0a0' }}>per unit / month</span>
              </p>
              <p className="text-sm mt-1 text-white" data-testid="quote-estimate-total">
                ≈ {range.low === range.high
                  ? `$${(range.low * units).toLocaleString()}`
                  : `$${(range.low * units).toLocaleString()} – $${(range.high * units).toLocaleString()}`} / month for {units} units
              </p>
              {form.contract_term === '36' && (
                <p className="text-xs mt-1" style={{ color: GOLD }}>Long-term commitment rate applied</p>
              )}
              <p className="text-xs mt-3 italic" style={{ color: '#a0a0a0' }} data-testid="quote-term-notice">
                Final pricing subject to management review and confirmation before contract execution.
              </p>
            </div>
          ) : null;
        })()}

        {error && <p className="mt-4 text-sm text-red-400" data-testid="quote-error">{error}</p>}

        <Button type="submit" disabled={submitting}
          className="w-full mt-6 font-black uppercase tracking-widest py-6 text-base"
          style={{ backgroundColor: GOLD, color: '#2D2D2D', fontFamily: 'Barlow Condensed, sans-serif' }}
          data-testid="quote-submit-btn">
          {submitting ? 'SUBMITTING…' : 'GET MY QUOTE'}
        </Button>
      </Card>
    </form>
  );
}
